import { useStytch, useStytchUser } from "@stytch/react";
import { PasswordResetBySessionOptions } from "@stytch/vanilla-js";
import { useMutation } from "@tanstack/react-query";
import { useAuthCancel, useAuthContinue } from "features/auth/AuthUtils";
import {
  AuthPasswordCreateForm,
  AuthPasswordCreateFormData,
} from "features/auth/password/AuthPasswordCreateForm";
import { getStytchSessionDurationMinutes } from "features/auth/StytchUtils";
import React from "react";
import { Navigate } from "react-router-dom";

const useStytchCreatePassword = () => {
  const stytchClient = useStytch();
  return useMutation({
    mutationFn: async (options: PasswordResetBySessionOptions) => {
      const user = stytchClient.user.getSync();
      if (!user) {
        throw new Error("Stytch user not found");
      }
      await stytchClient.passwords.resetBySession(options);
      await stytchClient.passwords.authenticate({
        email: user.emails[0].email,
        password: options.password,
        session_duration_minutes: getStytchSessionDurationMinutes(),
      });
    },
  });
};

export const AuthPasswordSetupPage: React.FC = () => {
  const { user: stytchUser } = useStytchUser();
  const authCancel = useAuthCancel();
  const authContinue = useAuthContinue();

  const { mutateAsync: createPassword, isPending: isCreatingPassword } =
    useStytchCreatePassword();

  const onSubmit = async ({ password }: AuthPasswordCreateFormData) => {
    await createPassword({
      password,
      session_duration_minutes: getStytchSessionDurationMinutes(),
    });
  };

  if (!stytchUser) {
    return <Navigate to="/auth/login" replace={true} />;
  }

  // User can't get to this page without an email
  const email = stytchUser.emails[0].email;

  return (
    <AuthPasswordCreateForm
      email={email}
      onSubmit={onSubmit}
      onSuccess={authContinue}
      onCancel={authCancel}
      isLoading={isCreatingPassword}
    />
  );
};
